import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "./Layout";
import { Building, Users, UserCheck, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    totalHotels: 0,
    totalClients: 0,
    totalExecutives: 0,
    myClients: 0,
    clientsByStatus: {}
  });
  const [loading, setLoading] = useState(true);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      console.log(`[Dashboard] Starting fetchDashboardStats for user: ${user.username} (${user.role})`);
      
      // Initialize default values
      let hotels = [];
      let clients = [];
      let executives = [];
      let myClients = 0;
      
      // Fetch hotels with individual error handling
      try {
        console.log('[Dashboard] Fetching hotels...');
        const hotelsResponse = await axios.get(`${API}/hotels`);
        hotels = hotelsResponse.data;
        console.log(`[Dashboard] Hotels fetched successfully: ${hotels.length} hotels`);
      } catch (error) {
        console.error('[Dashboard] Hotels fetch failed:', error);
        console.error('[Dashboard] Hotels API Error Details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        // Continue with empty hotels array
      }
      
      // Fetch clients with individual error handling
      try {
        console.log('[Dashboard] Fetching clients...');
        const clientsResponse = await axios.get(`${API}/clients?limit=1000`);
        clients = clientsResponse.data;
        console.log(`[Dashboard] Clients fetched successfully: ${clients.length} clients`);
      } catch (error) {
        console.error('[Dashboard] Clients fetch failed:', error);
        console.error('[Dashboard] Clients API Error Details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        // Continue with empty clients array
      }

      // Fetch executives with individual error handling
      if (user.role === "admin" || user.role === "manager") {
        try {
          console.log('[Dashboard] Fetching executives...');
          const executivesResponse = await axios.get(`${API}/executives`);
          executives = executivesResponse.data;
          console.log(`[Dashboard] Executives fetched successfully: ${executives.length} executives`);
        } catch (error) {
          console.error('[Dashboard] Executives fetch failed:', error);
          console.error('[Dashboard] Executives API Error Details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
          });
          // Continue with empty executives array
        }
      }
      
      // Calculate myClients based on role
      if (user.role === "admin") {
        myClients = clients.length;
      } else if (user.role === "manager") {
        myClients = clients.length; // All clients accessible to manager
        console.log(`[Dashboard] Manager myClients calculated: ${myClients}`);
      } else {
        // For executives, count their own clients
        myClients = clients.filter(client => client.executive_id === user.id).length;
        console.log(`[Dashboard] Executive myClients calculated: ${myClients}`);
      }
      
      // Calculate client status distribution
      try {
        console.log('[Dashboard] Calculating client status distribution...');
        const statusCounts = clients.reduce((acc, client) => {
          const status = client.client_status || 'Unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});
        
        console.log('[Dashboard] Status distribution calculated:', statusCounts);
        
        setStats({
          totalHotels: hotels.length,
          totalClients: clients.length,
          totalExecutives: executives.length,
          myClients: myClients,
          clientsByStatus: statusCounts
        });
        
        console.log('[Dashboard] Dashboard stats set successfully:', {
          totalHotels: hotels.length,
          totalClients: clients.length,
          totalExecutives: executives.length,
          myClients: myClients,
          userRole: user.role
        });
        
      } catch (error) {
        console.error('[Dashboard] Error calculating stats:', error);
        // Set empty stats as fallback
        setStats({
          totalHotels: 0,
          totalClients: 0,
          totalExecutives: 0,
          myClients: 0,
          clientsByStatus: {}
        });
      }
      
    } catch (error) {
      console.error('[Dashboard] General error in fetchDashboardStats:', error);
      // Even if everything fails, ensure we're not in loading state
      setStats({
        totalHotels: 0,
        totalClients: 0,
        totalExecutives: 0,
        myClients: 0,
        clientsByStatus: {}
      });
    } finally {
      console.log('[Dashboard] Dashboard loading completed');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, [user.id, user.role]);

  const ClientStatusCard = () => {
    const totalClients = stats.totalClients;
    const statusData = Object.entries(stats.clientsByStatus).map(([status, count]) => ({
      status,
      count,
      percentage: totalClients > 0 ? ((count / totalClients) * 100).toFixed(1) : 0
    }));

    const getStatusColor = (status) => {
      const colors = {
        'Active': 'text-green-600 bg-green-100',
        'Prospect': 'text-blue-600 bg-blue-100', 
        'Contacted': 'text-yellow-600 bg-yellow-100',
        'Qualified': 'text-purple-600 bg-purple-100',
        'Inactive': 'text-gray-600 bg-gray-100'
      };
      return colors[status] || 'text-gray-600 bg-gray-100';
    };

    return (
      <div className="stat-card" data-testid="stat-client-status">
        <div className="stat-icon clients">
          <TrendingUp size={24} />
        </div>
        <div className="stat-number">
          {loading ? (
            <div className="loading-spinner"></div>
          ) : (
            <div className="space-y-1">
              {statusData.length > 0 ? (
                statusData.map(({ status, count, percentage }) => (
                  <div key={status} className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-1 rounded-full ${getStatusColor(status)}`}>
                      {status}
                    </span>
                    <span className="font-semibold">{percentage}%</span>
                  </div>
                ))
              ) : (
                <span className="text-gray-500">No clients</span>
              )}
            </div>
          )}
        </div>
        <div className="stat-label">Client Status Distribution</div>
      </div>
    );
  };

  const StatCard = ({ title, value, icon: Icon, color, testId }) => (
    <div className="stat-card" data-testid={testId}>
      <div className={`stat-icon ${color}`}>
        <Icon size={24} />
      </div>
      <div className="stat-number">
        {loading ? (
          <div className="loading-spinner"></div>
        ) : (
          value.toLocaleString()
        )}
      </div>
      <div className="stat-label">{title}</div>
    </div>
  );

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    let greeting = "Good morning";
    if (hour >= 12 && hour < 18) greeting = "Good afternoon";
    if (hour >= 18) greeting = "Good evening";
    
    return `${greeting}, ${user.name}!`;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h2 className="page-title" data-testid="welcome-message">
          {getWelcomeMessage()}
        </h2>
        <p className="page-description">
          Welcome to your Click Efficiency CRM dashboard. Here's an overview of your system.
        </p>
      </div>

      <div className="stats-grid">
        {user.role === "admin" ? (
          <>
            <StatCard
              title="Total Hotels"
              value={stats.totalHotels}
              icon={Building}
              color="hotels"
              testId="stat-hotels"
            />
            <StatCard
              title="Total Clients"
              value={stats.totalClients}
              icon={Users}
              color="clients"
              testId="stat-clients"
            />
            <StatCard
              title="Total Executives"
              value={stats.totalExecutives}
              icon={UserCheck}
              color="executives"
              testId="stat-executives"
            />
            <ClientStatusCard />
          </>
        ) : user.role === "manager" ? (
          <>
            <StatCard
              title="My Hotels"
              value={user.hotel_ids?.length || 0}
              icon={Building}
              color="hotels"
              testId="stat-hotels"
            />
            <StatCard
              title="Team Clients"
              value={stats.myClients}
              icon={Users}
              color="clients"
              testId="stat-clients"
            />
            <StatCard
              title="Team Executives"
              value={stats.totalExecutives - 1} // Excluding admins
              icon={UserCheck}
              color="executives"
              testId="stat-executives"
            />
            <ClientStatusCard />
          </>
        ) : (
          <>
            <StatCard
              title="My Hotels"
              value={user.hotel_ids?.length || 0}
              icon={Building}
              color="hotels"
              testId="stat-my-hotels"
            />
            <StatCard
              title="My Clients"
              value={stats.myClients}
              icon={Users}
              color="clients"
              testId="stat-my-clients"
            />
            <ClientStatusCard />
          </>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">System Overview</h3>
        </div>
        <div className="card-content">
          <div style={{ display: "grid", gap: "1rem" }}>
            <div style={{ padding: "1rem", background: "#f9fafb", borderRadius: "8px" }}>
              <h4 style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                Role: {user.role === "admin" ? "Administrador" : user.role === "manager" ? "Manager" : "Ejecutivo"}
              </h4>
              <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                {user.role === "admin" 
                  ? "Tienes acceso completo para gestionar hoteles, ejecutivos y clientes. También puedes exportar datos."
                  : user.role === "manager"
                  ? `Puedes ver y gestionar clientes de ejecutivos en tus hoteles asignados. Tienes acceso a ${user.hotel_ids?.length || 0} hotel(es).`
                  : `Puedes gestionar clientes de tus hoteles asignados. Tienes acceso a ${user.hotel_ids?.length || 0} hotel(es).`
                }
              </p>
            </div>
            
            {user.role === "admin" && (
              <div style={{ padding: "1rem", background: "#fef3c7", borderRadius: "8px" }}>
                <h4 style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                  Admin Features
                </h4>
                <ul style={{ color: "#92400e", fontSize: "0.875rem", paddingLeft: "1rem" }}>
                  <li>Manage hotel catalog and executive assignments</li>
                  <li>Create and manage executive accounts</li>
                  <li>Export client data in Excel and CSV formats</li>
                  <li>View audit logs and system activity</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;